import {
  BrandCategoriesApiItemType,
  BrandOrderRequirementType,
  PurchaseTermsNamedItemType,
  RuleGroupConfigType,
  RuleGroupInactiveFilterItemType,
} from 'features/Enterprise/Vendor/types/PurchaseTerms'
import { filterActiveBrandScopeConfigs } from 'features/Enterprise/Vendor/services/purchaseTerms/buildRuleConfigsQueryParam'
import { isBrandScopeRelatedInCatalog } from './brandCascadeFilters'
import { BrandCsvParsedRowType } from './parseBrandTermsCsv'

type CatalogNameMapsType = {
  brandNameById: Map<number, string>
  l1NameById: Map<number, string>
  l2NameById: Map<number, string>
}

const buildCatalogNameMaps = (catalog: BrandCategoriesApiItemType[]): CatalogNameMapsType => {
  const brandNameById = new Map<number, string>()
  const l1NameById = new Map<number, string>()
  const l2NameById = new Map<number, string>()

  catalog.forEach((brand) => {
    brandNameById.set(brand.brand_id, brand.brand_name)
    ;(brand.categories ?? []).forEach((l1Category) => {
      l1NameById.set(l1Category.category_id, l1Category.category_name)
      const subcategories = l1Category.subcategories ?? []
      subcategories.forEach((l2Category) => {
        l2NameById.set(l2Category.category_id, l2Category.category_name)
      })
    })
  })

  return { brandNameById, l1NameById, l2NameById }
}

const resolveBrandNamedItems = (
  items: PurchaseTermsNamedItemType[],
  nameById: Map<number, string>,
): { items: PurchaseTermsNamedItemType[]; hasMissing: boolean } => {
  let hasMissing = false
  const resolved = items.map((item) => {
    const name = nameById.get(item.id)
    if (name === undefined) hasMissing = true
    return { id: item.id, name: name ?? item.name }
  })
  return { items: resolved, hasMissing }
}

const resolveCategoryNamedItems = (
  items: RuleGroupInactiveFilterItemType[],
  nameById: Map<number, string>,
): { items: RuleGroupInactiveFilterItemType[]; hasMissing: boolean } => {
  let hasMissing = false
  const resolved = items.map((item) => {
    const itemId = item.id
    if (itemId == null || itemId <= 0) {
      return { id: item.id, name: item.name }
    }
    const name = nameById.get(itemId)
    if (name === undefined) hasMissing = true
    return { id: itemId, name: name ?? item.name }
  })
  return { items: resolved, hasMissing }
}

export const getMissingCatalogIdsReason = (missingFieldKeys: string[]) => {
  if (missingFieldKeys.length === 0) return ''
  return `${missingFieldKeys.join(' dan ')} tidak ditemukan untuk vendor ini`
}

export type ResolveBrandCsvRowNamesResultType = {
  rows: BrandCsvParsedRowType[]
  failedRows: { rowNumber: number; reason: string }[]
}

export const resolveBrandCsvRowNames = (
  rows: BrandCsvParsedRowType[],
  catalog: BrandCategoriesApiItemType[],
): ResolveBrandCsvRowNamesResultType => {
  const { brandNameById, l1NameById, l2NameById } = buildCatalogNameMaps(catalog)
  const failedRows: { rowNumber: number; reason: string }[] = []

  const resolvedRows = rows.reduce<BrandCsvParsedRowType[]>((acc, row) => {
    const brands = resolveBrandNamedItems(row.brands, brandNameById)
    const l1Categories = resolveCategoryNamedItems(row.l1Categories, l1NameById)
    const l2Categories = resolveCategoryNamedItems(row.l2Categories, l2NameById)

    if (row.action === 'Delete') {
      acc.push({
        ...row,
        brands: brands.items,
        l1Categories: l1Categories.items,
        l2Categories: l2Categories.items,
      })
      return acc
    }

    const missingFieldKeys: string[] = []
    if (brands.hasMissing) missingFieldKeys.push('brand_id')
    if (l1Categories.hasMissing) missingFieldKeys.push('l1_category_id')
    if (l2Categories.hasMissing) missingFieldKeys.push('l2_category_id')
    const hasMissingIds = missingFieldKeys.length > 0
    const hasUnrelatedParentChild =
      !hasMissingIds &&
      !isBrandScopeRelatedInCatalog(catalog, {
        brands: brands.items,
        l1Categories: l1Categories.items,
        l2Categories: l2Categories.items,
      })

    if (hasMissingIds) {
      failedRows.push({
        rowNumber: row.rowNumber,
        reason: getMissingCatalogIdsReason(missingFieldKeys),
      })
      return acc
    }

    if (hasUnrelatedParentChild) {
      failedRows.push({
        rowNumber: row.rowNumber,
        reason: 'brand / L1 / L2 tidak berhubungan',
      })
      return acc
    }

    acc.push({
      ...row,
      brands: brands.items,
      l1Categories: l1Categories.items,
      l2Categories: l2Categories.items,
    })
    return acc
  }, [])

  return { rows: resolvedRows, failedRows }
}

const toNamedItemFromId = (
  id: number | undefined,
  name: string | undefined,
): PurchaseTermsNamedItemType | null => {
  if (!id || id <= 0) return null
  return { id, name: name?.trim() ?? '' }
}

const uniqueNamedItems = (items: PurchaseTermsNamedItemType[]) => {
  const byId = new Map<number, PurchaseTermsNamedItemType>()
  items.forEach((item) => {
    byId.set(item.id, item)
  })
  return Array.from(byId.values())
}

const filterScopeItemsByActiveConfigs = <TItem extends RuleGroupInactiveFilterItemType>(
  items: TItem[],
  pickId: (config: RuleGroupConfigType) => number | undefined,
  activeConfigs: RuleGroupConfigType[],
): TItem[] => {
  const allowedIds = new Set<number>()
  activeConfigs.forEach((config) => {
    const id = pickId(config)
    if (id && id > 0) allowedIds.add(id)
  })
  if (allowedIds.size === 0) return items
  return items.filter(
    (scopeItem) => scopeItem.id != null && scopeItem.id > 0 && allowedIds.has(scopeItem.id),
  )
}

const namedItemsFromConfigs = (
  configs: RuleGroupConfigType[],
  getId: (config: RuleGroupConfigType) => number | undefined,
  getName: (config: RuleGroupConfigType) => string | undefined,
) =>
  uniqueNamedItems(
    filterActiveBrandScopeConfigs(configs).flatMap((config) => {
      const item = toNamedItemFromId(getId(config), getName(config))
      return item ? [item] : []
    }),
  )

const buildNameMapFromConfigs = (
  configs: RuleGroupConfigType[],
  getId: (config: RuleGroupConfigType) => number | undefined,
  getName: (config: RuleGroupConfigType) => string | undefined,
) => {
  const nameById = new Map<number, string>()
  filterActiveBrandScopeConfigs(configs).forEach((config) => {
    const id = getId(config)
    const name = getName(config)?.trim()
    if (id && id > 0 && name) {
      nameById.set(id, name)
    }
  })
  return nameById
}

const fillEmptyNamesFromMap = <TItem extends RuleGroupInactiveFilterItemType>(
  items: TItem[],
  nameById: Map<number, string>,
): TItem[] =>
  items.map((item) => {
    if (item.name.trim()) return item
    const itemId = item.id
    if (itemId == null || itemId <= 0) return item
    const nameFromConfig = nameById.get(itemId)
    return nameFromConfig ? ({ id: itemId, name: nameFromConfig } as TItem) : item
  })

export const resolveBrandScopeItemsFromRequirement = (item: BrandOrderRequirementType) => {
  const brands = item.brands?.filter((brand) => brand.id > 0) ?? []
  const l1Categories = item.l1Categories?.filter((category) => (category.id ?? 0) > 0) ?? []
  const l2Categories = item.l2Categories?.filter((category) => (category.id ?? 0) > 0) ?? []
  const hasExplicitScope = brands.length > 0 || l1Categories.length > 0 || l2Categories.length > 0
  const allConfigs = item.rule_group_configs
  const activeConfigs = allConfigs?.length ? filterActiveBrandScopeConfigs(allConfigs) : []

  if (!hasExplicitScope && activeConfigs.length) {
    return {
      brands: namedItemsFromConfigs(
        activeConfigs,
        (config) => config.brand_id,
        (config) => config.brand_name,
      ),
      l1Categories: namedItemsFromConfigs(
        activeConfigs,
        (config) => config.l1_category_id,
        (config) => config.l1_category_name,
      ),
      l2Categories: namedItemsFromConfigs(
        activeConfigs,
        (config) => config.l2_category_id,
        (config) => config.l2_category_name,
      ),
    }
  }

  if (hasExplicitScope && activeConfigs.length) {
    const visibleBrands = filterScopeItemsByActiveConfigs(
      brands,
      (config) => config.brand_id,
      activeConfigs,
    )
    const visibleL1Categories = filterScopeItemsByActiveConfigs(
      l1Categories,
      (config) => config.l1_category_id,
      activeConfigs,
    )
    const visibleL2Categories = filterScopeItemsByActiveConfigs(
      l2Categories,
      (config) => config.l2_category_id,
      activeConfigs,
    )

    return {
      brands: fillEmptyNamesFromMap(
        visibleBrands,
        buildNameMapFromConfigs(
          activeConfigs,
          (config) => config.brand_id,
          (config) => config.brand_name,
        ),
      ),
      l1Categories: fillEmptyNamesFromMap(
        visibleL1Categories,
        buildNameMapFromConfigs(
          activeConfigs,
          (config) => config.l1_category_id,
          (config) => config.l1_category_name,
        ),
      ),
      l2Categories: fillEmptyNamesFromMap(
        visibleL2Categories,
        buildNameMapFromConfigs(
          activeConfigs,
          (config) => config.l2_category_id,
          (config) => config.l2_category_name,
        ),
      ),
    }
  }

  return { brands, l1Categories, l2Categories }
}

export const hydrateBrandOrderRequirementScope = (
  requirement: BrandOrderRequirementType,
): BrandOrderRequirementType => {
  const scopeItems = resolveBrandScopeItemsFromRequirement(requirement)
  return {
    ...requirement,
    brands: scopeItems.brands,
    l1Categories: scopeItems.l1Categories,
    l2Categories: scopeItems.l2Categories,
  }
}

export const resolveBrandRequirementFromCatalog = (
  item: BrandOrderRequirementType,
  catalog: BrandCategoriesApiItemType[],
): BrandOrderRequirementType => {
  const scopeItems = resolveBrandScopeItemsFromRequirement(item)
  if (!catalog.length) {
    return { ...item, ...scopeItems }
  }

  const { brandNameById, l1NameById, l2NameById } = buildCatalogNameMaps(catalog)

  return {
    ...item,
    brands: resolveBrandNamedItems(scopeItems.brands, brandNameById).items,
    l1Categories: resolveCategoryNamedItems(scopeItems.l1Categories, l1NameById).items,
    l2Categories: resolveCategoryNamedItems(scopeItems.l2Categories, l2NameById).items,
  }
}
